import type { MessagesConfig } from '@/api/installation.api';

/**
 * Catálogo PURO de la pestaña Mensajes (rediseño de configuración): cada
 * plantilla con su título humano, su sección, el disparador del ruteo cuyo
 * switch la enciende/apaga (SIEMPRE uno existente — aquí no se inventan
 * switches) y las variables que el sistema rellena solo.
 *
 * Fuente de verdad de la UI: las tarjetas, los toggles y los chips de
 * variables se pintan desde esta lista.
 */

export type TemplateSeccion = 'cliente' | 'interno' | 'tecnico';

export interface TemplateVariable {
  /** Token crudo tal como viaja en la plantilla ({{cliente}}). */
  token: string;
  /** Etiqueta humana del chip ("Nombre del cliente"). */
  label: string;
}

export interface MessageTemplateMeta {
  /** Clave de MessagesConfig (el texto vive en la config de la instalación). */
  key: keyof MessagesConfig;
  /** Título humano de la tarjeta. */
  titulo: string;
  /** Cuándo se envía (una línea honesta, lenguaje del dueño). */
  cuando: string;
  /**
   * Disparador del ruteo cuyo switch controla este mensaje.
   *
   * `null` SOLO para las plantillas OBLIGATORIAS, que no se pueden apagar: hoy,
   * las confirmaciones de baja y de re-alta. Apagar la confirmación de una baja
   * es no avisarle al cliente que su pedido quedó registrado, y quien no recibe
   * esa confirmación cree que lo ignoraron y reporta el número.
   */
  trigger: string | null;
  seccion: TemplateSeccion;
  variables: TemplateVariable[];
  /** Alto sugerido del editor (filas). */
  rows: number;
}

const V = {
  cliente: { token: '{{cliente}}', label: 'Nombre del cliente' },
  tecnico: { token: '{{tecnico}}', label: 'Nombre del técnico' },
  zona: { token: '{{zona}}', label: 'Zona del trabajo' },
  descripcion: { token: '{{descripcion}}', label: 'Descripción del trabajo' },
  fecha: { token: '{{fecha}}', label: 'Fecha de la cita' },
  hora: { token: '{{hora}}', label: 'Hora' },
  motivo: { token: '{{motivo}}', label: 'Motivo reportado' },
  horas: { token: '{{horas}}', label: 'Horas de la ventana' },
  telefono: { token: '{{telefono}}', label: 'Teléfono del cliente' },
  modelo: { token: '{{modelo}}', label: 'Modelo de interés' },
  respuesta: { token: '{{respuesta}}', label: 'Respuesta del cliente' },
  direccion: { token: '{{direccion}}', label: 'Dirección' },
  alcance: { token: '{{alcance}}', label: 'Alcance del tope' },
  gastado: { token: '{{gastado}}', label: 'Monto gastado' },
  tope: { token: '{{tope}}', label: 'Tope configurado' },
  cantidad: { token: '{{cantidad}}', label: 'Cuántas garantías van' },
  ventana: { token: '{{ventana}}', label: 'Período de cobertura mirado' },
  distancia: { token: '{{distancia}}', label: 'Distancia al destino' },
  codigo: { token: '{{codigo}}', label: 'Código del pendiente (P-0001)' },
  quote: { token: '{{quote}}', label: 'Lo que se prometió, textual' },
  cita: { token: '{{cita}}', label: 'Fecha y hora de la cita' },
} as const;

export const TEMPLATE_SECTIONS: Record<TemplateSeccion, { titulo: string; hint: string }> = {
  cliente: {
    titulo: 'Mensajes al cliente',
    hint: 'Le llegan al cliente del trabajo por WhatsApp. El switch los enciende o apaga; el destino no cambia.',
  },
  interno: {
    titulo: 'Avisos internos',
    hint: 'Le llegan al dueño (o a los números configurados abajo en «¿A quién le llegan los avisos?»).',
  },
  tecnico: {
    titulo: 'Avisos al técnico',
    hint: 'Le llegan al teléfono del técnico asignado (su ficha de empleado).',
  },
};

export const MESSAGE_TEMPLATE_CATALOG: MessageTemplateMeta[] = [
  // ------------------------------ Cliente ------------------------------
  {
    key: 'aiFallback',
    titulo: 'Cortesía cuando la IA no puede responder',
    cuando:
      'Se envía si el asistente falla o se queda sin respuesta, para no dejar al cliente en visto.',
    trigger: 'cortesia_fallback',
    seccion: 'cliente',
    variables: [],
    rows: 2,
  },
  {
    key: 'appointmentReminder',
    titulo: 'Recordatorio de cita',
    cuando: 'Se envía antes de la cita agendada del trabajo.',
    trigger: 'recordatorio_cita',
    seccion: 'cliente',
    variables: [V.cliente, V.tecnico, V.fecha, V.hora, V.descripcion],
    rows: 3,
  },
  {
    key: 'technicianArrived',
    titulo: 'Aviso: llegó el técnico',
    cuando: 'Se envía cuando el técnico marca «Llegué».',
    trigger: 'aviso_llegada',
    seccion: 'cliente',
    variables: [V.cliente, V.tecnico],
    rows: 2,
  },
  {
    key: 'survey',
    titulo: 'Encuesta después del servicio',
    cuando: 'Se envía al completar un trabajo, para saber cómo quedó el cliente.',
    trigger: 'encuesta',
    seccion: 'cliente',
    variables: [V.cliente],
    rows: 3,
  },
  {
    key: 'surveyRemote',
    titulo: 'Encuesta de un caso resuelto a distancia',
    cuando: 'Se envía al cerrar una garantía resuelta por teléfono, sin visita del técnico.',
    trigger: 'encuesta_remota',
    seccion: 'cliente',
    variables: [V.cliente],
    rows: 3,
  },
  {
    key: 'surveyThanksGood',
    titulo: 'Respuesta a encuesta positiva',
    cuando:
      'Respuesta automática cuando el cliente contesta conforme. Comparte switch con las otras dos respuestas.',
    trigger: 'respuestas_encuesta',
    seccion: 'cliente',
    variables: [V.cliente],
    rows: 2,
  },
  {
    key: 'surveyThanksBad',
    titulo: 'Respuesta a una queja',
    cuando: 'Respuesta automática ante una queja: reconoce y avisa que un encargado se comunica.',
    trigger: 'respuestas_encuesta',
    seccion: 'cliente',
    variables: [V.cliente],
    rows: 3,
  },
  {
    key: 'surveyThanksNeutral',
    titulo: 'Respuesta neutral (acuse)',
    cuando: 'Acuse cordial cuando la respuesta no es claramente buena ni mala.',
    trigger: 'respuestas_encuesta',
    seccion: 'cliente',
    variables: [V.cliente],
    rows: 2,
  },
  // ------------------------------ Interno ------------------------------
  {
    key: 'slaOverdueOwner',
    titulo: 'SLA vencido',
    cuando: 'El técnico lleva demasiado tiempo en el sitio sin cerrar el trabajo.',
    trigger: 'sla_vencido',
    seccion: 'interno',
    variables: [V.tecnico, V.cliente, V.zona, V.descripcion, V.horas],
    rows: 3,
  },
  {
    key: 'jobNotCompletedOwner',
    titulo: 'Trabajo no completado',
    cuando: 'El técnico marcó «No terminé» con su motivo.',
    trigger: 'trabajo_no_completado',
    seccion: 'interno',
    variables: [V.tecnico, V.cliente, V.motivo],
    rows: 3,
  },
  {
    key: 'noShowOwner',
    titulo: 'Cliente no llegó',
    cuando:
      'El técnico reporta que el cliente no se presentó. Cancelar o reagendar se decide en el panel.',
    trigger: 'no_show',
    seccion: 'interno',
    variables: [V.tecnico, V.cliente, V.descripcion, V.zona],
    rows: 3,
  },
  // BAJAS. Sin switch a propósito (`trigger: null`): son la respuesta
  // obligatoria a un cliente que pidió no recibir más mensajes, o que pidió
  // volver a recibirlos. Se puede cambiar el texto, no apagarlas.
  {
    key: 'optOutConfirmation',
    titulo: 'Confirmación de baja',
    cuando:
      'El cliente escribe "baja", "stop" o "no me escriban". Se le confirma una sola vez y no se le vuelve a mandar nada proactivo. Si escribe otra cosa, se lo atiende igual.',
    trigger: null,
    seccion: 'cliente',
    variables: [V.cliente],
    rows: 3,
  },
  {
    key: 'optInConfirmation',
    titulo: 'Confirmación de re-alta',
    cuando: 'Un cliente dado de baja escribe "ALTA" (solo esa palabra). Vuelve a recibir mensajes.',
    trigger: null,
    seccion: 'cliente',
    variables: [V.cliente],
    rows: 2,
  },
  // Las dos alertas de reincidencia. Van separadas porque son dos avisos
  // distintos con dos switches distintos: el dueño puede apagar el del cliente
  // (más ruidoso) sin perder el de el artículo, que alimenta el reporte.
  {
    key: 'garantiaReincidenteInstalacionOwner',
    titulo: 'La misma artículo volvió a fallar',
    cuando:
      'Se abre una garantía de un artículo que ya había fallado dentro de su período de cobertura. El plazo se cuenta desde la falla anterior, no desde la instalación.',
    trigger: 'garantia_reincidente_instalacion',
    seccion: 'interno',
    variables: [V.cliente, V.telefono, V.modelo, V.cantidad, V.ventana, V.descripcion],
    rows: 4,
  },
  {
    key: 'garantiaReincidenteOwner',
    titulo: 'Cliente con garantías repetidas',
    cuando: 'El mismo cliente abre otra garantía teniendo todavía algún equipo en cobertura.',
    trigger: 'garantia_reincidente',
    seccion: 'interno',
    variables: [V.cliente, V.telefono, V.cantidad, V.ventana, V.descripcion],
    rows: 4,
  },
  // Pendientes del Vendedor. El destino NO se elige en el ruteo: cada aviso va
  // al teléfono del vendedor dueño del pendiente. El switch sí manda.
  {
    key: 'pendienteVentaVendedor',
    titulo: 'Pendiente de VENTA del vendedor',
    cuando:
      'Se cumple la fecha de un compromiso ligado a una venta. Le llega al vendedor a cargo, a su propio teléfono.',
    trigger: 'pendiente_vendedor_venta',
    seccion: 'interno',
    variables: [V.codigo, V.descripcion, V.cliente, V.telefono, V.quote],
    rows: 5,
  },
  {
    key: 'pendienteVendedor',
    titulo: 'Pendiente del vendedor',
    cuando:
      'Se cumple la fecha de un compromiso con un cliente. Le llega al vendedor a cargo, a su propio teléfono.',
    trigger: 'pendiente_vendedor',
    seccion: 'interno',
    variables: [V.codigo, V.descripcion, V.cliente, V.telefono, V.quote],
    rows: 5,
  },
  {
    key: 'cierreFueraDeRadioOwner',
    titulo: 'Cierre fuera de la zona del cliente',
    cuando: 'El técnico cerró un trabajo lejos del punto de destino. Sale al momento.',
    trigger: 'cierre_fuera_de_radio',
    seccion: 'interno',
    variables: [V.tecnico, V.cliente, V.distancia, V.direccion],
    rows: 3,
  },
  {
    key: 'resumenDiarioTrabajos',
    titulo: 'Resumen diario de trabajos',
    cuando:
      'Encabezado del mensaje de las 8 PM. Los números del día los arma el sistema debajo de este texto.',
    trigger: 'resumen_diario_trabajos',
    seccion: 'interno',
    variables: [V.fecha],
    rows: 2,
  },
  {
    key: 'surveyBadAlertOwner',
    titulo: 'Alerta de encuesta negativa',
    cuando: 'Un cliente respondió la encuesta con una queja.',
    trigger: 'encuesta_negativa',
    seccion: 'interno',
    variables: [V.respuesta],
    rows: 3,
  },
  {
    key: 'hotLeadOwner',
    titulo: 'Lead caliente',
    cuando: 'El asistente escaló una conversación a CALIENTE: hay una venta a punto.',
    trigger: 'lead_caliente',
    seccion: 'interno',
    variables: [V.cliente, V.telefono, V.modelo, V.zona],
    rows: 3,
  },
  {
    key: 'tecnicoLlegoOwner',
    titulo: 'El técnico llegó',
    cuando: 'Aviso de movimiento: el técnico ponchó llegada donde el cliente.',
    trigger: 'tecnico_llego',
    seccion: 'interno',
    variables: [V.tecnico, V.cliente, V.direccion, V.zona, V.hora],
    rows: 2,
  },
  {
    key: 'tecnicoTerminoOwner',
    titulo: 'El técnico terminó',
    cuando: 'Aviso de movimiento: el técnico cerró el trabajo.',
    trigger: 'tecnico_termino',
    seccion: 'interno',
    variables: [V.tecnico, V.cliente, V.direccion, V.zona, V.hora],
    rows: 2,
  },
  {
    key: 'spendCapReachedOwner',
    titulo: 'Tope de gasto de IA alcanzado',
    cuando: 'El asistente dejó de responder por IA hasta mañana; los mensajes se siguen guardando.',
    trigger: 'tope_gasto',
    seccion: 'interno',
    variables: [V.alcance, V.gastado, V.tope],
    rows: 3,
  },
  // ------------------------------ Técnico ------------------------------
  {
    key: 'slaOverdueTechnician',
    titulo: 'SLA vencido (recordatorio al técnico)',
    cuando: 'Le pide al técnico ponchar su salida o pedir ayuda.',
    trigger: 'sla_vencido_tecnico',
    seccion: 'tecnico',
    variables: [V.tecnico, V.cliente, V.descripcion, V.horas],
    rows: 3,
  },
  {
    key: 'trabajoAsignadoTecnico',
    titulo: 'Trabajo asignado',
    cuando: 'Le avisa al técnico que se le asignó (o reasignó) un trabajo. Nace apagado.',
    trigger: 'trabajo_asignado_tecnico',
    seccion: 'tecnico',
    variables: [V.tecnico, V.cliente, V.zona, V.cita, V.direccion, V.descripcion],
    rows: 5,
  },
];
